import React, { useState, useEffect } from 'react';
import { Activity } from 'lucide-react';
import Dashboard from './components/Dashboard';
import SignIn from './components/SignIn';
import Sidenav from './components/Sidenav';
import Events from './components/Events';
import Journeys from './components/Journeys';
import Billing from './components/Billing';
import Settings from './components/Settings';
import AcceptInvitation from './components/AcceptInvitation';
import Onboarding from './components/Onboarding';
import GettingStartedModal from './components/GettingStartedModal';
import { buildApiUrl } from './config/api';

interface EventMetadata {
  [key: string]: string;
}

interface Event {
  id: number;
  name: string;
  tracked_at: string;
  metadata: EventMetadata;
  project?: {
    id: number;
    name: string;
  };
  organization?: {
    id: number;
    name: string;
  };
}

interface ApiResponse {
  message: string;
  data: {
    events: Event[];
  };
}

interface Project {
  id: number;
  name: string;
  api_key: string;
  organization: {
    id: number;
    name: string;
  };
}

interface Organization {
  id: number;
  name: string;
  role: string;
  plan: 'trial' | 'pro' | 'business' | 'enterprise';
  events_used: number;
  plan_limit: number;
  events_remaining: number;
  usage_percentage: number;
  trial_active: boolean;
  trial_expired: boolean;
  trial_days_remaining: number;
  trial_ends_at?: string;
  over_limit: boolean;
  approaching_limit: boolean;
  can_track_events: boolean;
  projects: Project[];
}

interface User {
  id: number;
  email: string;
  name: string;
  organizations: Organization[];
}

type AppView = 'login' | 'dashboard' | 'journeys' | 'events' | 'billing' | 'settings' | 'accept-invitation' | 'onboarding';

function App() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<AppView>('login');
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [onboardingEmail, setOnboardingEmail] = useState<string>('');
  const [showGettingStartedModal, setShowGettingStartedModal] = useState(false);

  // Helper functions for URL routing
  const getViewFromPath = (pathname: string, isAuthenticated: boolean = false): AppView => {
    switch (pathname) {
      case '/':
        return isAuthenticated ? 'dashboard' : 'login';
      case '/dashboard':
        return 'dashboard';
      case '/journeys':
        return 'journeys';
      case '/events':
        return 'events';
      case '/billing':
        return 'billing';
      case '/settings':
        return 'settings';
      case '/accept-invitation':
        return 'accept-invitation';
      case '/onboarding':
        return 'onboarding';
      case '/onboarding/complete':
        return 'dashboard';
      default:
        return isAuthenticated ? 'dashboard' : 'login';
    }
  };

  const getPathFromView = (view: AppView): string => {
    switch (view) {
      case 'dashboard':
        return '/dashboard';
      case 'journeys':
        return '/journeys';
      case 'events':
        return '/events';
      case 'billing':
        return '/billing';
      case 'settings':
        return '/settings';
      case 'accept-invitation':
        return '/accept-invitation';
      case 'onboarding':
        return '/onboarding';
      case 'login':
      default:
        return '/';
    }
  };

  const isProtectedRoute = (pathname: string): boolean => {
    const protectedRoutes = ['/dashboard', '/journeys', '/events', '/billing', '/settings'];
    return protectedRoutes.includes(pathname);
  };

  const navigateToView = (view: AppView) => {
    const path = getPathFromView(view);
    window.history.pushState({}, '', path);
    setCurrentView(view);
  };

  // Save project to localStorage
  const saveProjectToStorage = (project: Project | null) => {
    if (project) {
      localStorage.setItem('turret_current_project_id', project.id.toString());
    } else {
      localStorage.removeItem('turret_current_project_id');
    }
  };

  // Load project from localStorage
  const loadProjectFromStorage = (projects: Project[]): Project | null => {
    const savedProjectId = localStorage.getItem('turret_current_project_id');
    if (savedProjectId) {
      const savedProject = projects.find(p => p.id.toString() === savedProjectId);
      if (savedProject) {
        return savedProject;
      }
    }
    return null;
  };

  // Enhanced project change handler that saves to localStorage
  const handleProjectChange = (project: Project) => {
    setCurrentProject(project);
    saveProjectToStorage(project);
  };

  // Handle URL-based routing and authentication
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const magicLinkToken = urlParams.get('token');
    const invitationToken = urlParams.get('invitation') || urlParams.get('invite');
    
    // Check if this is the accept-invitation route
    if (window.location.pathname === '/accept-invitation' || 
        window.location.pathname.includes('accept-invitation')) {
      const tokenToUse = magicLinkToken;
      if (tokenToUse) {
        setCurrentView('accept-invitation');
        setLoading(false);
        return;
      }
    }
    
    // Check for explicit invitation parameters
    if (invitationToken) {
      setCurrentView('accept-invitation');
      setLoading(false);
      return;
    }
    
    // Check if this is the onboarding completion route
    if (window.location.pathname === '/onboarding/complete' || 
        window.location.pathname.includes('onboarding/complete')) {
      const sessionId = urlParams.get('session_id');
      if (sessionId) {
        handleOnboardingCompletion(sessionId);
        return;
      }
    }
    
    if (magicLinkToken) {
      console.log('Magic link token found:', magicLinkToken);
      verifyMagicLink(magicLinkToken);
      // Clean up URL without reloading - will be handled by the verification result
    } else {
      // Check localStorage for existing JWT token
      const storedToken = localStorage.getItem('turret_token');
      console.log('Stored token:', storedToken);
      
      if (storedToken) {
        setToken(storedToken);
        // Set view based on current URL path
        const viewFromPath = getViewFromPath(window.location.pathname, true);
        if (window.location.pathname === '/') {
          // Redirect root to /dashboard for authenticated users
          navigateToView('dashboard');
        } else {
          setCurrentView(viewFromPath);
        }
      } else {
        setLoading(false);
        setCurrentView('login');
        // Redirect to login if trying to access protected route
        if (isProtectedRoute(window.location.pathname)) {
          window.history.replaceState({}, document.title, '/');
        }
      }
    }
  }, []);

  // Fetch user data when token is available
  useEffect(() => {
    if (token) {
      console.log('Token available, fetching user data');
      fetchUserData();
    }
  }, [token]);

  // Debug: Allow showing getting started modal with ?show_welcome=true
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('show_welcome') === 'true' && user && currentProject) {
      setShowGettingStartedModal(true);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user, currentProject]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const isAuthenticated = !!(user && token);
      const viewFromPath = getViewFromPath(window.location.pathname, isAuthenticated);
      
      // Check if user is trying to access protected route without auth
      if (isProtectedRoute(window.location.pathname) && !isAuthenticated) {
        window.history.replaceState({}, '', '/');
        setCurrentView('login');
        return;
      }
      
      // If authenticated user is on root, redirect to dashboard
      if (window.location.pathname === '/' && isAuthenticated) {
        navigateToView('dashboard');
        return;
      }
      
      setCurrentView(viewFromPath);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user, token]);

  // Update view based on authentication state (only set initial view, don't override navigation)
  useEffect(() => {
    if (user && token && currentView === 'login') {
      console.log('User and token available, switching to dashboard from login');
      navigateToView('dashboard');
    } else if (!loading && !user && !token && currentView !== 'accept-invitation' && currentView !== 'onboarding') {
      console.log('No user or token, switching to login');
      navigateToView('login');
    }
  }, [user, token, loading, currentView]);

  const verifyMagicLink = async (magicLinkToken: string) => {
    try {
      console.log('Verifying magic link token...');
      setLoading(true);
      
      const response = await fetch(buildApiUrl(`api/v1/auth/verify_magic_link?token=${magicLinkToken}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to verify magic link');
      }
      
      const data = await response.json();
      
      if (data.new_user) {
        // This is a new user - redirect to onboarding
        console.log('New user detected, redirecting to onboarding');
        setOnboardingEmail(data.email);
        navigateToView('onboarding');
        setError(null);
      } else {
        // Existing user - sign them in
        const jwtToken = data.token;
        
        console.log('Existing user, storing JWT token');
        
        // Store the JWT token
        localStorage.setItem('turret_token', jwtToken);
        setToken(jwtToken);
        
        setUser(data.user);
        setError(null);
        // Note: Navigation to dashboard will be handled by the authentication useEffect
      }
    } catch (err) {
      console.error('Error verifying magic link:', err);
      setError('Failed to verify magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    if (!token) return;
    
    try {
      console.log('Fetching user data with token...');
      const response = await fetch(buildApiUrl('api/v1/auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }
      
      const data = await response.json();
      
      console.log('User data fetched successfully');
      
      setUser(data.user);
      setError(null);
    } catch (err) {
      console.error('Error fetching user data:', err);
      // Token might be invalid, clear it
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(buildApiUrl('api/v1/events'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: ApiResponse = await response.json();
      setEvents(data.data.events);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log('Logging out...');
    localStorage.removeItem('turret_token');
    localStorage.removeItem('turret_current_project_id');
    setToken(null);
    setUser(null);
    setEvents([]);
    setCurrentProject(null);
    navigateToView('login');
    setError(null);
  };

  const handleSignInError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const clearError = () => {
    setError(null);
  };
  
  const handleInvitationAccepted = (jwtToken: string, userData: User) => {
    console.log('Invitation accepted, storing JWT token');
    
    // Store the JWT token
    localStorage.setItem('turret_token', jwtToken);
    setToken(jwtToken);
    setUser(userData);
    
    // Navigate to dashboard and show getting started modal for new team members
    navigateToView('dashboard');
    setShowGettingStartedModal(true);
    setError(null);
  };
  
  const handleOnboardingCompletion = async (sessionId: string) => {
    try {
      console.log('Completing onboarding with session ID:', sessionId);
      setLoading(true);
      
      const response = await fetch(buildApiUrl(`api/v1/onboarding/complete?session_id=${sessionId}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to complete onboarding');
      }
      
      const data = await response.json();
      const jwtToken = data.token;
      
      console.log('Onboarding completed, storing JWT token');
      
      // Store the JWT token
      localStorage.setItem('turret_token', jwtToken);
      setToken(jwtToken);
      setUser(data.user);
      
      // Navigate to dashboard and show getting started modal
      navigateToView('dashboard');
      setShowGettingStartedModal(true);
      setError(null);
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setError('Failed to complete setup. Please contact support.');
      setCurrentView('login');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOnboardingCompleted = (jwtToken: string, userData: User) => {
    console.log('Onboarding flow completed, storing JWT token');
    
    // Store the JWT token
    localStorage.setItem('turret_token', jwtToken);
    setToken(jwtToken);
    setUser(userData);
    
    // Navigate to dashboard and show getting started modal
    navigateToView('dashboard');
    setShowGettingStartedModal(true);
    setError(null);
  };

  // Show loading screen during authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Activity className="h-8 w-8 text-primary animate-pulse" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  // Show sign in screen if not authenticated
  if (currentView === 'login') {
    return (
      <div>
        <SignIn onError={handleSignInError} />
        {error && (
          <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-md shadow-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm">{error}</span>
              <button 
                onClick={clearError}
                className="ml-2 text-white hover:text-gray-200"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Show invitation acceptance screen
  if (currentView === 'accept-invitation') {
    const urlParams = new URLSearchParams(window.location.search);
    const invitationToken = urlParams.get('invitation') || urlParams.get('invite') || urlParams.get('token');
    
    if (invitationToken) {
      return (
        <AcceptInvitation 
          token={invitationToken} 
          onAccepted={handleInvitationAccepted}
        />
      );
    } else {
      // No token found, redirect to login
      setCurrentView('login');
      return null;
    }
  }
  
  // Show onboarding screen for new users
  if (currentView === 'onboarding') {
    // Allow testing onboarding with ?email=test@example.com query param
    const urlParams = new URLSearchParams(window.location.search);
    const testEmail = urlParams.get('email') || onboardingEmail || 'test@example.com';
    
    return (
      <Onboarding 
        email={testEmail}
        onComplete={handleOnboardingCompleted}
      />
    );
  }
  
  // Render main app layout with sidenav
  const renderMainContent = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <div className="flex-1 bg-background overflow-y-auto">
            <Dashboard 
              user={user!} 
              token={token!} 
              onLogout={logout}
              currentProject={currentProject}
              onRefreshUser={fetchUserData}
            />
          </div>
        );
      
      case 'journeys':
        return (
          <div className="flex-1 bg-background overflow-y-auto">
            <Journeys
              currentProject={currentProject}
              token={token!}
            />
          </div>
        );

      case 'events':
        return (
          <div className="flex-1 bg-background overflow-y-auto">
            <Events
              currentProject={currentProject}
              token={token!}
            />
          </div>
        );

      case 'billing':
        return (
          <div className="flex-1 bg-background overflow-y-auto">
            <Billing
              user={user!}
              token={token!}
              currentProject={currentProject}
            />
          </div>
        );
      
      case 'settings':
        return (
          <div className="flex-1 bg-background overflow-y-auto">
            <Settings
              user={user!}
              token={token!}
              currentProject={currentProject}
              onRefreshUser={fetchUserData}
            />
          </div>
        );
      
      default:
        return (
          <div className="flex-1 bg-background overflow-y-auto">
            <Dashboard 
              user={user!} 
              token={token!} 
              onLogout={logout}
              currentProject={currentProject}
              onRefreshUser={fetchUserData}
            />
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidenav
        user={user!}
        token={token!}
        currentView={currentView}
        onViewChange={(view) => navigateToView(view as AppView)}
        currentProject={currentProject}
        onProjectChange={handleProjectChange}
      />
      {renderMainContent()}
      
      {/* Getting Started Modal for new users */}
      {currentProject && user && (
        <GettingStartedModal
          isOpen={showGettingStartedModal}
          onClose={() => setShowGettingStartedModal(false)}
          apiKey={currentProject.api_key}
          organizationId={user.organizations[0]?.id}
          token={token!}
          projectName={currentProject.name}
        />
      )}
    </div>
  );
}

export default App; 